// ======================================================================
// FILE:
// src/modules/finance/transactions/manual_journal/services/journalService.js
//
// MODULE:
// Finance & Accounts Management System (FBAMS)
//
// FEATURE:
// Manual Journal
//
// PURPOSE:
// Frontend API Client for Finance Journal.
//
// RESPONSIBILITIES
// - Journal Header CRUD
// - Journal Workflow
// - Journal Line CRUD
//
// NOTES
// - Uses axiosClient only
// - No React code
// - No UI logic
// - No Toast
// - No Navigation
// - No State Management
//
// AUTHOR:
// DizyPro ERP
// ======================================================================

import axiosClient
from "../../../api/axiosClient";

// ======================================================================
// API BASE URL
// ======================================================================

const BASE_URL =
    "/api/finance/journals";

// ======================================================================
// JOURNAL HEADER
// ======================================================================

/**
 * ----------------------------------------------------------------------
 * LIST JOURNALS
 * ----------------------------------------------------------------------
 *
 * Backend
 * GET /api/finance/journals
 *
 */

async function listJournals(
    filters = {}
) {

    const response =
        await axiosClient.get(
            BASE_URL,
            {
                params: filters,
            }
        );

    return {

        rows:
            response.data.data.rows || [],

        summary:
            response.data.data.summary || {},

        pagination:
            response.data.data.pagination || {},

    };

}

/**
 * ----------------------------------------------------------------------
 * GET JOURNAL
 * ----------------------------------------------------------------------
 *
 * Backend
 * GET /api/finance/journals/{id}
 *
 */

async function getJournal(
    journalId
) {

    const response =
        await axiosClient.get(
            `${BASE_URL}/${journalId}`
        );

    return response.data.data;

}

/**
 * ----------------------------------------------------------------------
 * CREATE JOURNAL
 * ----------------------------------------------------------------------
 *
 * Backend
 * POST /api/finance/journals
 *
 */

async function createJournal(
    payload
) {

    const response =
        await axiosClient.post(
            BASE_URL,
            payload
        );

    return response.data.data;

}

/**
 * ----------------------------------------------------------------------
 * UPDATE JOURNAL
 * ----------------------------------------------------------------------
 *
 * Backend
 * PUT /api/finance/journals/{id}
 *
 */

async function updateJournal(
    journalId,
    payload
) {

    const response =
        await axiosClient.put(
            `${BASE_URL}/${journalId}`,
            payload
        );

    return response.data.data;

}

/**
 * ----------------------------------------------------------------------
 * DELETE JOURNAL
 * ----------------------------------------------------------------------
 *
 * Backend
 * DELETE /api/finance/journals/{id}
 *
 */

async function deleteJournal(
    journalId
) {

    const response =
        await axiosClient.delete(
            `${BASE_URL}/${journalId}`
        );

    return response.data;

}

// ======================================================================
// JOURNAL WORKFLOW
// ======================================================================

/**
 * ----------------------------------------------------------------------
 * VALIDATE JOURNAL
 * ----------------------------------------------------------------------
 *
 * Backend
 * POST /api/finance/journals/{id}/validate
 *
 */

async function validateJournal(
    journalId
) {

    const response =
        await axiosClient.post(
            `${BASE_URL}/${journalId}/validate`
        );

    return response.data.data;

}

/**
 * ----------------------------------------------------------------------
 * SUBMIT JOURNAL
 * ----------------------------------------------------------------------
 *
 * Backend
 * POST /api/finance/journals/{id}/submit
 *
 */

async function submitJournal(
    journalId
) {

    const response =
        await axiosClient.post(
            `${BASE_URL}/${journalId}/submit`
        );

    return response.data.data;

}

/**
 * ----------------------------------------------------------------------
 * APPROVE JOURNAL
 * ----------------------------------------------------------------------
 *
 * Backend
 * POST /api/finance/journals/{id}/approve
 *
 */

async function approveJournal(
    journalId
) {

    const response =
        await axiosClient.post(
            `${BASE_URL}/${journalId}/approve`
        );

    return response.data.data;

}

/**
 * ----------------------------------------------------------------------
 * REJECT JOURNAL
 * ----------------------------------------------------------------------
 *
 * Backend
 * POST /api/finance/journals/{id}/reject
 *
 */

async function rejectJournal(
    journalId
) {

    const response =
        await axiosClient.post(
            `${BASE_URL}/${journalId}/reject`
        );

    return response.data.data;

}

/**
 * ----------------------------------------------------------------------
 * POST JOURNAL
 * ----------------------------------------------------------------------
 *
 * Backend
 * POST /api/finance/journals/{id}/post
 *
 */

async function postJournal(
    journalId
) {

    const response =
        await axiosClient.post(
            `${BASE_URL}/${journalId}/post`
        );

    return response.data.data;

}
// ======================================================================
// JOURNAL LINES
// ======================================================================

/**
 * ----------------------------------------------------------------------
 * LIST JOURNAL LINES
 * ----------------------------------------------------------------------
 *
 * Backend
 * GET /api/finance/journals/{journalId}/lines
 *
 */

async function listJournalLines(
    journalId
) {

    const response =
        await axiosClient.get(
            `${BASE_URL}/${journalId}/lines`
        );

    return response.data.data;

}

/**
 * ----------------------------------------------------------------------
 * GET JOURNAL LINE
 * ----------------------------------------------------------------------
 *
 * Backend
 * GET /api/finance/journals/lines/{lineId}
 *
 */

async function getJournalLine(
    journalLineId
) {

    const response =
        await axiosClient.get(
            `${BASE_URL}/lines/${journalLineId}`
        );

    return response.data.data;

}

/**
 * ----------------------------------------------------------------------
 * CREATE JOURNAL LINE
 * ----------------------------------------------------------------------
 *
 * Backend
 * POST /api/finance/journals/{journalId}/lines
 *
 */

async function createJournalLine(
    journalId,
    payload
) {

    const response =
        await axiosClient.post(
            `${BASE_URL}/${journalId}/lines`,
            payload
        );

    return response.data.data;

}

/**
 * ----------------------------------------------------------------------
 * UPDATE JOURNAL LINE
 * ----------------------------------------------------------------------
 *
 * Backend
 * PUT /api/finance/journals/lines/{lineId}
 *
 */

async function updateJournalLine(
    journalLineId,
    payload
) {

    const response =
        await axiosClient.put(
            `${BASE_URL}/lines/${journalLineId}`,
            payload
        );

    return response.data.data;

}

/**
 * ----------------------------------------------------------------------
 * DELETE JOURNAL LINE
 * ----------------------------------------------------------------------
 *
 * Backend
 * DELETE /api/finance/journals/lines/{lineId}
 *
 */

async function deleteJournalLine(
    journalLineId
) {

    const response =
        await axiosClient.delete(
            `${BASE_URL}/lines/${journalLineId}`
        );

    return response.data;

}

// ======================================================================
// EXPORT
// ======================================================================

const journalService = {

    // --------------------------------------------------------------
    // Journal Header
    // --------------------------------------------------------------

    listJournals,

    getJournal,

    createJournal,

    updateJournal,

    deleteJournal,

    // --------------------------------------------------------------
    // Workflow
    // --------------------------------------------------------------

    validateJournal,

    submitJournal,

    approveJournal,

    rejectJournal,

    postJournal,

    // --------------------------------------------------------------
    // Journal Lines
    // --------------------------------------------------------------

    listJournalLines,

    getJournalLine,

    createJournalLine,

    updateJournalLine,

    deleteJournalLine,

};

export default journalService;